/**
 * StoragePort — document-scoped source/media object storage.
 *
 * The okra document runtime persists the original PDF (`source`) and any derived
 * media (page images, figure crops) behind THIS port, so the same orchestrator
 * runs against R2 (cloud + self-deploy) or the DocumentAgent's own SQLite
 * (zero-dependency self-deploy default) without branching on tenancy.
 *
 * Keys are workspace-prefixed for cloud-compatibility:
 *   source: `${workspaceId}/${documentId}/source.pdf`
 *   media:  `${workspaceId}/${documentId}/media/${name}`
 *
 * Platform-agnostic per `@okrapdf/core` harness rules: the R2 bucket and the
 * DO-SQLite executor are typed STRUCTURALLY (minimal shapes), never imported
 * from `@cloudflare/*`.
 *
 * SigV4 presigning (for direct-to-R2 uploads) uses `aws4fetch` — a pure,
 * Web-Crypto-based npm package (no `@cloudflare/*`), so the harness import
 * boundary stays intact.
 */
import { AwsClient } from 'aws4fetch';
import { Clock, Context, Data, Effect } from 'effect';

/** Identifies a document within a workspace. `workspaceId='local'` for single-player. */
export interface DocKey {
  workspaceId: string;
  documentId: string;
}

/**
 * Source + media object storage for one document graph.
 * Implementations choose the backend (R2, DO SQLite, fs); callers stay blind.
 */
export interface StoragePort {
  /** Store the original document bytes. */
  putSource(k: DocKey, bytes: Uint8Array): Promise<void>;
  /** Read the original document bytes, or null if absent. */
  getSource(k: DocKey): Promise<Uint8Array | null>;
  /** Store a derived media artifact (page image, figure crop, …). */
  putMedia(k: DocKey, name: string, bytes: Uint8Array, contentType: string): Promise<void>;
  /** Read a derived media artifact, or null if absent. */
  getMedia(k: DocKey, name: string): Promise<{ bytes: Uint8Array; contentType: string } | null>;
  /**
   * Mint a presigned URL the client can `PUT` the source bytes to DIRECTLY,
   * bypassing the Worker body (emdash-style true signed upload). Returns `null`
   * when the backend cannot presign (e.g. DO-SQLite) — the caller then falls
   * back to the multipart upload path. Only R2-with-S3-credentials supports it.
   */
  getSignedUploadUrl?(
    k: DocKey,
    contentType: string,
    expiresInSeconds?: number,
  ): Promise<SignedUpload | null>;
}

/** A presigned direct-to-storage PUT the client executes itself. */
export interface SignedUpload {
  /** Absolute URL to PUT the bytes to (SigV4 query-signed). */
  url: string;
  /** Always `PUT` for a presigned source upload. */
  method: 'PUT';
  /** Headers the client MUST send so the request matches the signature. */
  headers: Record<string, string>;
  /** Epoch-ms after which the signature is no longer valid. */
  expiresAt: number;
}

const TypeId = 'okrapdf/core/storage/DocumentStorage/TypeId' as const;
const ErrorTypeId = 'okrapdf/core/storage/DocumentStorage/DocumentStorageError' as const;

export interface DocumentMedia {
  readonly bytes: Uint8Array;
  readonly contentType: string;
}

/** Native Effect contract for document-scoped source and media storage. */
export interface DocumentStorage {
  readonly [TypeId]: typeof TypeId;
  readonly putSource: (key: DocKey, bytes: Uint8Array) => Effect.Effect<void, DocumentStorageError>;
  readonly getSource: (key: DocKey) => Effect.Effect<Uint8Array | null, DocumentStorageError>;
  readonly putMedia: (
    key: DocKey,
    name: string,
    bytes: Uint8Array,
    contentType: string,
  ) => Effect.Effect<void, DocumentStorageError>;
  readonly getMedia: (
    key: DocKey,
    name: string,
  ) => Effect.Effect<DocumentMedia | null, DocumentStorageError>;
  readonly getSignedUploadUrl: (
    key: DocKey,
    contentType: string,
    expiresInSeconds?: number,
  ) => Effect.Effect<SignedUpload | null, DocumentStorageError>;
}

export type DocumentStorageMethod =
  | 'putSource'
  | 'getSource'
  | 'putMedia'
  | 'getMedia'
  | 'getSignedUploadUrl';

export class DocumentStorageError extends Data.TaggedError('DocumentStorageError')<{
  readonly method: DocumentStorageMethod;
  readonly message: string;
  readonly key: DocKey;
  readonly mediaName?: string;
  readonly cause?: unknown;
}> {
  readonly [ErrorTypeId]: typeof ErrorTypeId = ErrorTypeId;
}

export const DocumentStorage: Context.Service<DocumentStorage, DocumentStorage> = Context.Service(
  'okrapdf/core/storage/DocumentStorage',
);

export interface DocumentStorageMakeOptions {
  readonly putSource: (key: DocKey, bytes: Uint8Array) => Effect.Effect<void, DocumentStorageError>;
  readonly getSource: (key: DocKey) => Effect.Effect<Uint8Array | null, DocumentStorageError>;
  readonly putMedia: (
    key: DocKey,
    name: string,
    bytes: Uint8Array,
    contentType: string,
  ) => Effect.Effect<void, DocumentStorageError>;
  readonly getMedia: (
    key: DocKey,
    name: string,
  ) => Effect.Effect<DocumentMedia | null, DocumentStorageError>;
  readonly getSignedUploadUrl?: (
    key: DocKey,
    contentType: string,
    expiresInSeconds?: number,
  ) => Effect.Effect<SignedUpload | null, DocumentStorageError>;
}

/** Construct the native Effect service from storage operations. */
export function makeDocumentStorage(options: DocumentStorageMakeOptions): DocumentStorage {
  return DocumentStorage.of({
    [TypeId]: TypeId,
    putSource: options.putSource,
    getSource: options.getSource,
    putMedia: options.putMedia,
    getMedia: options.getMedia,
    getSignedUploadUrl:
      options.getSignedUploadUrl ?? (() => Effect.succeed<SignedUpload | null>(null)),
  });
}

function storageError(
  method: DocumentStorageMethod,
  key: DocKey,
  cause: unknown,
  mediaName?: string,
): DocumentStorageError {
  return new DocumentStorageError({
    method,
    key,
    mediaName,
    message: cause instanceof Error ? cause.message : String(cause),
    cause,
  });
}

function attemptPromise<A>(
  method: DocumentStorageMethod,
  key: DocKey,
  evaluate: () => PromiseLike<A>,
  mediaName?: string,
): Effect.Effect<A, DocumentStorageError> {
  return Effect.tryPromise({
    try: evaluate,
    catch: (cause) => storageError(method, key, cause, mediaName),
  });
}

function attemptSync<A>(
  method: DocumentStorageMethod,
  key: DocKey,
  evaluate: () => A,
  mediaName?: string,
): Effect.Effect<A, DocumentStorageError> {
  return Effect.try({
    try: evaluate,
    catch: (cause) => storageError(method, key, cause, mediaName),
  });
}

// ── key helpers (workspace-prefixed = cloud-compat) ────────────────────────

export function sourceKey(k: DocKey): string {
  return `${k.workspaceId}/${k.documentId}/source.pdf`;
}

export function mediaKey(k: DocKey, name: string): string {
  return `${k.workspaceId}/${k.documentId}/media/${name}`;
}

// ── R2 adapter ─────────────────────────────────────────────────────────────

/** Minimal structural HTTP metadata shared by the R2 storage adapters. */
export interface R2HttpMetadataLike {
  contentType?: string;
}

/** Minimal structural shape of R2 object metadata. */
export interface R2ObjectLike {
  size?: number;
  httpMetadata?: R2HttpMetadataLike;
}

/** Minimal structural shape of an R2 object body we read. */
export interface R2ObjectBodyLike extends R2ObjectLike {
  arrayBuffer(): Promise<ArrayBuffer>;
}

/** Minimal structural shape of an R2 bucket binding (avoids a @cloudflare/* import). */
export interface R2BucketLike {
  get(key: string): Promise<R2ObjectBodyLike | null>;
  put(
    key: string,
    value: ArrayBuffer | Uint8Array | ReadableStream | string,
    options?: { httpMetadata?: R2HttpMetadataLike },
  ): Promise<unknown>;
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  // Copy into a standalone ArrayBuffer so a view over a larger/shared buffer
  // (or a SharedArrayBuffer) is never handed to the R2 binding.
  return bytes.slice().buffer;
}

/** Native R2-backed document storage. */
export function r2DocumentStorage(bucket: R2BucketLike): DocumentStorage {
  return makeDocumentStorage({
    putSource: (key, bytes) =>
      attemptPromise('putSource', key, () =>
        bucket.put(sourceKey(key), toArrayBuffer(bytes), {
          httpMetadata: { contentType: 'application/pdf' },
        }),
      ).pipe(Effect.asVoid),
    getSource: (key) =>
      Effect.gen(function* () {
        const object = yield* attemptPromise('getSource', key, () => bucket.get(sourceKey(key)));
        if (!object) return null;
        const buffer = yield* attemptPromise('getSource', key, () => object.arrayBuffer());
        return new Uint8Array(buffer);
      }),
    putMedia: (key, name, bytes, contentType) =>
      attemptPromise(
        'putMedia',
        key,
        () =>
          bucket.put(mediaKey(key, name), toArrayBuffer(bytes), {
            httpMetadata: { contentType },
          }),
        name,
      ).pipe(Effect.asVoid),
    getMedia: (key, name) =>
      Effect.gen(function* () {
        const object = yield* attemptPromise(
          'getMedia',
          key,
          () => bucket.get(mediaKey(key, name)),
          name,
        );
        if (!object) return null;
        const buffer = yield* attemptPromise('getMedia', key, () => object.arrayBuffer(), name);
        return {
          bytes: new Uint8Array(buffer),
          contentType: object.httpMetadata?.contentType ?? 'application/octet-stream',
        };
      }),
  });
}

// ── R2 presigned (direct-to-R2 upload) adapter ─────────────────────────────

/** S3-API credentials for R2 — needed to SigV4-presign (the binding can't). */
export interface R2S3Credentials {
  /** Cloudflare account id (the `<accountId>.r2.cloudflarestorage.com` host). */
  accountId: string;
  /** R2 S3 access key id. */
  accessKeyId: string;
  /** R2 S3 secret access key. */
  secretAccessKey: string;
  /** Bucket name (e.g. `okra-solo`). */
  bucket: string;
}

/** Default presigned-URL lifetime (seconds). */
const DEFAULT_PRESIGN_EXPIRY_SECONDS = 600;

/**
 * R2-backed StoragePort that ALSO supports presigned direct-to-R2 uploads.
 *
 * Reads/writes (`putSource`/`getSource`/`putMedia`/`getMedia`) delegate to the
 * ordinary `r2Storage(bucket)` binding behavior. `getSignedUploadUrl` SigV4-signs
 * a PUT to R2's S3 endpoint so a browser can upload the source PDF straight to
 * R2, bypassing the Worker body (and its size cap). Cloud and self-deploy share
 * this once the three R2 S3 secrets are bound.
 */
export function r2PresignedDocumentStorage(
  bucket: R2BucketLike,
  creds: R2S3Credentials,
): DocumentStorage {
  const base = r2DocumentStorage(bucket);
  const aws = new AwsClient({
    accessKeyId: creds.accessKeyId,
    secretAccessKey: creds.secretAccessKey,
    service: 's3',
    region: 'auto',
  });
  return makeDocumentStorage({
    ...base,
    getSignedUploadUrl: (key, contentType, expiresInSeconds = DEFAULT_PRESIGN_EXPIRY_SECONDS) =>
      Effect.gen(function* () {
        const objectKey = sourceKey(key);
        // R2 S3-compatible endpoint. Encode each path segment so the workspace/
        // document ids survive (they're slug-safe today, but stay correct if not).
        const encodedKey = objectKey.split('/').map(encodeURIComponent).join('/');
        const endpoint = `https://${creds.accountId}.r2.cloudflarestorage.com/${creds.bucket}/${encodedKey}`;
        const url = new URL(endpoint);
        // Query-sign so the credential rides the URL (no Authorization header needed
        // client-side); `X-Amz-Expires` sets the TTL. UNSIGNED-PAYLOAD so the body
        // bytes aren't hashed into the signature — the client streams them straight.
        url.searchParams.set('X-Amz-Expires', String(expiresInSeconds));
        const signed = yield* attemptPromise('getSignedUploadUrl', key, () =>
          aws.sign(url.toString(), {
            method: 'PUT',
            headers: {
              'content-type': contentType,
              'x-amz-content-sha256': 'UNSIGNED-PAYLOAD',
            },
            aws: { signQuery: true, allHeaders: false },
          }),
        );
        const now = yield* Clock.currentTimeMillis;
        return {
          url: signed.url,
          method: 'PUT',
          headers: { 'content-type': contentType },
          expiresAt: now + expiresInSeconds * 1000,
        };
      }),
  });
}

// ── DO-SQLite adapter ──────────────────────────────────────────────────────

/**
 * Minimal structural shape of the Agents-SDK `this.sql` tagged template
 * (synchronous, returns rows). Typed here so the adapter needs no `agents`
 * or `@cloudflare/*` import.
 */
export type DoSql = <T = Record<string, unknown>>(
  strings: TemplateStringsArray,
  ...values: unknown[]
) => T[];

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
}

/**
 * DocumentAgent-SQLite StoragePort. Mirrors the current self-host `source`
 * table behavior (one base64 PDF row, `id=1`) and extends it with a `media`
 * table for derived artifacts — all inside the DO's own SQLite, so the
 * single-player default needs no R2 bucket.
 *
 * The `DocKey` is implied by the DO instance (one DocumentAgent per document),
 * matching the existing single-row `source` semantics; key fields are accepted
 * for interface parity but not used to scope rows within the DO.
 */
export function doSqliteDocumentStorage(sql: DoSql): DocumentStorage {
  let ensured = false;
  const ensure = (method: DocumentStorageMethod, key: DocKey, mediaName?: string) =>
    attemptSync(
      method,
      key,
      () => {
        if (ensured) return;
        sql`CREATE TABLE IF NOT EXISTS source (id INTEGER PRIMARY KEY CHECK (id = 1), b64 TEXT NOT NULL)`;
        sql`CREATE TABLE IF NOT EXISTS media (name TEXT PRIMARY KEY, content_type TEXT NOT NULL, b64 TEXT NOT NULL)`;
        ensured = true;
      },
      mediaName,
    );

  return makeDocumentStorage({
    putSource: (key, bytes) =>
      Effect.gen(function* () {
        yield* ensure('putSource', key);
        yield* attemptSync('putSource', key, () => {
          const b64 = bytesToBase64(bytes);
          sql`INSERT OR REPLACE INTO source (id, b64) VALUES (1, ${b64})`;
        });
      }),
    getSource: (key) =>
      Effect.gen(function* () {
        yield* ensure('getSource', key);
        return yield* attemptSync('getSource', key, () => {
          const rows = sql<{ b64: string }>`SELECT b64 FROM source WHERE id = 1`;
          return rows.length ? base64ToBytes(rows[0].b64) : null;
        });
      }),
    putMedia: (key, name, bytes, contentType) =>
      Effect.gen(function* () {
        yield* ensure('putMedia', key, name);
        yield* attemptSync(
          'putMedia',
          key,
          () => {
            const b64 = bytesToBase64(bytes);
            sql`INSERT OR REPLACE INTO media (name, content_type, b64) VALUES (${name}, ${contentType}, ${b64})`;
          },
          name,
        );
      }),
    getMedia: (key, name) =>
      Effect.gen(function* () {
        yield* ensure('getMedia', key, name);
        return yield* attemptSync(
          'getMedia',
          key,
          () => {
            const rows = sql<{
              content_type: string;
              b64: string;
            }>`SELECT content_type, b64 FROM media WHERE name = ${name}`;
            if (!rows.length) return null;
            return { bytes: base64ToBytes(rows[0].b64), contentType: rows[0].content_type };
          },
          name,
        );
      }),
    // A DO's own SQLite has no presignable endpoint — signal "unsupported" so the
    // caller falls back to the multipart (through-the-Worker) upload path.
    getSignedUploadUrl: () => Effect.succeed(null),
  });
}

function runStorage<A>(effect: Effect.Effect<A, DocumentStorageError>): Promise<A> {
  return Effect.runPromise(effect.pipe(Effect.catch((error) => Effect.fail(error.cause))));
}

export interface StoragePortFromDocumentStorageOptions {
  /** Preserve the legacy optional-method shape for backends that cannot presign. */
  readonly includeSignedUpload?: boolean;
}

/** Promise compatibility adapter derived from the native Effect service. */
export function storagePortFromDocumentStorage(
  storage: DocumentStorage,
  options: StoragePortFromDocumentStorageOptions = {},
): StoragePort {
  const port: StoragePort = {
    putSource: (key, bytes) => runStorage(storage.putSource(key, bytes)),
    getSource: (key) => runStorage(storage.getSource(key)),
    putMedia: (key, name, bytes, contentType) =>
      runStorage(storage.putMedia(key, name, bytes, contentType)),
    getMedia: (key, name) => runStorage(storage.getMedia(key, name)),
  };
  return options.includeSignedUpload === false
    ? port
    : {
        ...port,
        getSignedUploadUrl: (key, contentType, expiresInSeconds) =>
          runStorage(storage.getSignedUploadUrl(key, contentType, expiresInSeconds)),
      };
}

/** Promise R2 adapter retained for compatibility. */
export function r2Storage(bucket: R2BucketLike): StoragePort {
  return storagePortFromDocumentStorage(r2DocumentStorage(bucket), {
    includeSignedUpload: false,
  });
}

/** Promise presigned-R2 adapter retained for compatibility. */
export function r2PresignedStorage(bucket: R2BucketLike, creds: R2S3Credentials): StoragePort {
  return storagePortFromDocumentStorage(r2PresignedDocumentStorage(bucket, creds));
}

/** Promise DO-SQLite adapter retained for compatibility. */
export function doSqliteStorage(sql: DoSql): StoragePort {
  return storagePortFromDocumentStorage(doSqliteDocumentStorage(sql));
}
